// @ts-strict-ignore
import {produce} from 'immer';
import {AttrsSchema, Component} from 'panel';
import partition from 'lodash/partition';
import moment from 'moment';

import {capitalize, pluralize} from 'common/util/string';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {Visibility} from 'common/components/base/stackable';
import {apiClient} from 'common/util/api-client';
import {getServiceAccountNameErrorMessage} from 'common/util/service-accounts';
import {Permission} from 'common/report/constants';
import {reporter} from 'common/report/error-reporting';
import {
  addOrgUsers,
  addOrgUsersWithAttachment,
  addUsersToOrgProjects,
  addUsersToOrgTeams,
  fetchDomainData,
  fetchOrgProjectData,
  fetchOrgTeamData,
  fetchOrgUserData,
  getOrgAssignableOrgRoles,
  getOrgAssignableProjectRoles,
} from 'common/report/queries/app-api/legacy/orgs-settings-client';
import {events, properties, track} from 'common/report/util/settings-tracking';
import {abbreviateStrings, isValidEmailAddress, isVerifiedDomain} from 'common/util/string';
import {CreateServiceAccountResult} from 'app_api/organizations/service_accounts/types';
import {ExpiresModeOptions, ExpiresUnit} from 'common/types/reports/service-accounts';
import {DetailedUser} from 'common/report/queries/app-api/legacy/org-types';
import {getLocation} from 'common/util/url';
import {IronContextRegistry} from 'common/report/context-registry';
import {UserRole} from 'common/report/util/saved-entities/types';
import {
  UserRoleI18nKey,
  UserRoleOrgDescriptionI18nKey,
  UserRoleProjectDescriptionI18nKey,
} from 'common/report/util/saved-entities/constants';

import {template} from './template';
import css from './index.inline.styl';
import {
  InviteUsersModalAttrsSchema,
  InviteUsersModalJsxProps,
  InviteUsersModalState,
  KeyedByOrg,
  PartialProject,
  TABS,
  UserRow,
  UserType,
} from './types';

let currUserRowId = 0;

export function usersToProjectsAndTeams(users): any {
  const projects = {};
  const teams = {};

  users.forEach((u) => {
    (u.projectIds ?? []).forEach((userProject) => {
      if (projects[userProject]) {
        projects[userProject].push({email: u.email, role: u.projectRole});
      } else {
        projects[userProject] = [{email: u.email, role: u.projectRole}];
      }
    });
    (u.teamNames ?? []).forEach((teamName) => {
      if (teams[teamName]) {
        teams[teamName].push(u.email);
      } else {
        teams[teamName] = [u.email];
      }
    });
  });

  return {
    projects: Object.entries(projects).map(([id, projectUsers]) => ({
      id: parseInt(id, 10),
      users: projectUsers,
    })),
    teams: Object.entries(teams).map(([name, userEmails]) => ({
      name,
      userEmails,
    })),
  };
}

export class InviteUsersModal extends Component<
  InviteUsersModalState,
  InviteUsersModalAttrsSchema,
  null,
  null,
  IronContextRegistry
> {
  static get attrsSchema(): AttrsSchema<InviteUsersModalAttrsSchema> {
    return {
      ...super.attrsSchema,
      open: {type: `boolean`, description: `Should the modal be open?`},
      'exclude-teams': {type: `boolean`},
      'exclude-bulk-invite': {type: `boolean`},
      'exclude-project-selections': {type: `boolean`},
      'user-type': {type: `string`, default: UserType.User, enum: Object.values(UserType)},
    };
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.isServiceAccount()) {
      // default org role and project role
      const updatedUserRows = this.state.userRows.map((row) => {
        return {
          ...row,
          orgRole: UserRole.Member,
          projectRole: UserRole.Consumer,
        };
      });
      this.update({userRows: updatedUserRows});
    }
  }

  getBlankUserRow(): UserRow {
    return {
      id: ++currUserRowId,
      email: null,
      ...this.getOrgSwitchPartialUserRow(),
    };
  }

  getOrgSwitchPartialUserRow(): Omit<UserRow, `id` | `email`> {
    const singleProjectId = this.state ? this.getProjectIdIfSingleProject() : null;

    return {
      orgRole: null,
      projectIds: singleProjectId ? [singleProjectId] : [],
      projectRole: null,
      teamNames: [],
      orgRoleError: false,
      projectError: false,
      projectRoleError: false,
      emailError: false,
      emailErrorMsg: null,
      emailInfo: false,
      projectIdsToDowngrade: [],
      seeAll: false,
      error: null,
    };
  }

  getCurrentOrgProjectData() {
    return (
      (this.state.orgProjectRoles[this.state.selectedOrg.id] &&
        this.state.orgProjectRoles[this.state.selectedOrg.id].projects) ||
      {}
    );
  }

  get i18n() {
    return this.getContext(`i18n`);
  }

  get t() {
    return this.i18n.t;
  }

  get print() {
    return this.i18n.print;
  }

  get config() {
    return {
      css,
      template,
      contexts: [`i18n` as const],
      useShadowDom: true,

      defaultState: {
        currentTab: TABS.PROJECT,
        isLoading: false,
        isOrgProjectsProp: false,
        orgDomains: {},
        orgProjectRoles: {},
        orgProjects: {},
        orgRoles: {},
        orgs: [],
        orgTeams: {},
        orgUsers: {},
        selectedOrg: null,
        serviceAccount: null,
        userFile: {},
        userRows: [this.getBlankUserRow()],
      },

      helpers: {
        // tab management
        TABS,
        selectTab: (ev) => {
          const {userRows, userFile} = this.config.defaultState;
          this.update({
            currentTab: ev.detail.selected,
            userRows, // reset user rows/file on tab change
            userFile,
          });
        },

        isServiceAccount: () => {
          return this.isServiceAccount();
        },

        // after copying username and secret for service accounts
        handleClickedDone: () => {
          if (this.isServiceAccount()) {
            this.dispatchEvent(
              new CustomEvent(`notify`, {
                detail: {
                  message: this.t(`settings.org-settings.invite-users.service-account-created`),
                },
              }),
            );
          }
          this.dispatchEvent(new CustomEvent(`done`));
        },

        // email input
        updateUserEmail: (ev, row: UserRow) => {
          const userEmail = ev.target.value ?? ``;
          this.updateUserRow(row, {
            email: userEmail.trim(),
          });
        },

        // name input
        updateServiceAccountName: (ev, row: UserRow) => {
          const name = ev.target.value;
          const nameErrorMsg = getServiceAccountNameErrorMessage(name);
          const nameError = !!nameErrorMsg;
          this.updateUserRow(row, {
            name,
            nameError,
            nameErrorMsg,
          });
        },
        hasInvalidName: () => {
          // we only create one service account at a time
          const row = this.state.userRows[0];
          return row.name === null || !!getServiceAccountNameErrorMessage(row.name);
        },
        serviceAccountNameValidationStatus: (row: UserRow) => {
          return !row.name && row.name !== `` ? `` : row.nameError ? `invalid` : `valid`;
        },

        // org input
        getOrgSections: () => [
          {
            items: this.state.orgs.map((o) => ({...o, label: o.name})),
          },
        ],
        selectOrg: (ev) => {
          if (!this.state.selectedOrg || ev.detail.item.id !== this.state.selectedOrg.id) {
            this.update({
              selectedOrg: ev.detail.item,
              userRows: this.state.userRows.map((row) => ({...row, ...this.getOrgSwitchPartialUserRow()})),
            });
            this.syncData();
          }
        },

        // org role input
        getOrgRoleSections: (row: UserRow) => {
          return [
            {
              items: (this.state.orgRoles[this.state.selectedOrg.id] || []).map((orgRole) => ({
                ...orgRole,
                value: orgRole.name,
                isSelected: row.orgRole === orgRole.name,
                label: this.t(UserRoleI18nKey[orgRole.name]),
                subLabel: this.t(UserRoleOrgDescriptionI18nKey[orgRole.name]),
                wrapSubLabel: true,
              })),
            },
          ];
        },
        selectOrgRole: (ev, row: UserRow) => {
          this.updateUserRow(row, {
            orgRole: ev.detail.item.name,
          });
        },

        // project input
        getProjectSections: (row: UserRow) => [
          {
            items: (this.state.orgProjects[this.state.selectedOrg.id] || [])
              .filter((project) => this.canInviteToProject(project))
              .map((project) => ({
                ...project,
                label: project.name,
                isSelected: row.projectIds.includes(project.id),
              })),
          },
        ],
        toggleProjectSelected: (ev, row: UserRow) => {
          const id = ev.detail.item.id;
          const pids = row.projectIds;
          const projectIds = pids.includes(id) ? pids.filter((pid) => pid !== id) : pids.concat(id);
          this.updateUserRow(row, {
            projectIds,
            projectIdsToDowngrade: this.helpers.getProjectIdsToDowngrade(row, null, projectIds),
          });
        },
        toggleAllProjectsSelected: (ev, row: UserRow) => {
          const projects = this.state.orgProjects[this.state.selectedOrg.id] || [];
          const projectIds = ev.detail.selectionState === `all` ? [] : projects.map((p) => p.id);
          this.updateUserRow(row, {
            projectIds,
            projectIdsToDowngrade: this.helpers.getProjectIdsToDowngrade(row, null, projectIds),
          });
        },
        // project role input
        getProjectRoleSections: () => {
          const roleMap = {};

          Object.values(this.getCurrentOrgProjectData()).forEach((roleArray: any) =>
            roleArray
              // deduplicate
              .forEach((role) => (roleMap[role.name] = role)),
          );

          return [
            {
              items: Object.values(roleMap).map((pr: any) => ({
                ...pr,
                label: this.t(UserRoleI18nKey[pr.name]),
                subLabel: UserRoleProjectDescriptionI18nKey[pr.name]
                  ? this.t(UserRoleProjectDescriptionI18nKey[pr.name])
                  : ``,
                wrapSubLabel: true,
              })),
            },
          ];
        },
        getProjectIdsToDowngrade: (row: UserRow, roleName = null, projectIds = null) => {
          if (!roleName && !row.projectRole) {
            return [];
          }
          return (projectIds || row.projectIds).filter((projectId) =>
            this.getCurrentOrgProjectData()[projectId].every((role) => role.name !== (roleName || row.projectRole)),
          );
        },

        selectProjectRole: (ev, row: UserRow) => {
          const roleName = ev.detail.item.name;
          this.updateUserRow(row, {
            projectRole: roleName,
            projectIdsToDowngrade: this.helpers.getProjectIdsToDowngrade(row, roleName),
          });
        },

        // team input
        // TODO: should really only show teams that the user has perms to invite to
        getTeamSections: (row: UserRow) => [
          {
            items: (this.state.orgTeams[this.state.selectedOrg.id] || []).map((team) => ({
              ...team,
              label: team.name,
              isSelected: row.teamNames.includes(team.name),
            })),
          },
        ],
        toggleTeamSelected: (ev, row: UserRow) => {
          const name = ev.detail.item.name;
          const teams = row.teamNames;
          this.updateUserRow(row, {
            teamNames: teams.includes(name) ? teams.filter((t) => t !== name) : teams.concat(name),
          });
        },
        toggleAllTeamsSelected: (ev, row: UserRow) => {
          const teams = this.state.orgTeams[this.state.selectedOrg.id] || [];
          this.updateUserRow(row, {
            teamNames: ev.detail.selectionState === `all` ? [] : teams.map((t) => t.name),
          });
        },

        // error management
        updateEmailError: (ev, row: UserRow) => {
          this.updateUserRow(
            row,
            this.validateEmail({
              ...row,
              email: ev.target.value,
            }),
          );
        },
        clearErrors: () => {
          this.update({
            userRows: this.state.userRows.map((row) => ({
              ...row,
              orgRoleError: false,
              projectError: false,
              projectRoleError: false,
              emailError: false,
              emailErrorMsg: row.emailInfo ? row.emailErrorMsg : null,
              nameError: false,
              nameErrorMsg: null,
            })),
          });
        },

        // button click handlers
        clickedAddMoreUser: () => {
          this.update({
            userRows: [...this.state.userRows, this.getBlankUserRow()],
          });
        },
        clickedDeleteButton: (row: UserRow) => {
          this.update({
            userRows: this.state.userRows.filter((r) => r.id !== row.id),
          });
        },
        clickedSave: async () => {
          if (this.isServiceAccount()) {
            this.update(this.validateCreateServiceAccountForm());
            if (this.allRowsValid()) {
              this.update({isLoading: true});
              await this.createServiceAccount(this.state.userRows);
            }
          } else {
            switch (this.state.currentTab) {
              case TABS.PROJECT:
              case TABS.TEAM: {
                this.update(this.validateAddUsersForm());
                if (this.allRowsValid()) {
                  this.update({isLoading: true});
                  await this.sendInvitation(this.state.userRows);
                }
                break;
              }
              case TABS.BULK: {
                this.update(this.validateBulkAddForm());
                if (!this.state.userFile.error) {
                  this.update({isLoading: true});
                  await this.sendCsvFile(this.state.userFile.csvFile);
                }
                break;
              }
            }
          }

          this.update({isLoading: false});
        },
        clickedCancel: () => {
          this.dispatchEvent(new CustomEvent(`cancel`));
        },

        // status change listeners
        fileStatusChanged: (ev) => {
          if (ev.detail && ev.detail.file) {
            const file = ev.detail.file;
            this.update({userFile: {name: file.name, csvFile: file}});
          }
        },
        removeSelectedFile: () => {
          this.update({userFile: {}});
        },
        modalVisibilityChanged: (ev) => {
          if (ev.detail && ev.detail.state === Visibility.Closed) {
            // once modal close animation is complete, reset UI state
            const {currentTab, userRows, userFile} = this.config.defaultState;
            this.update({currentTab, userRows, userFile});
          } else {
            // sync data on modal open
            this.syncData();
          }
        },

        // template data formatting
        capitalize,
        pluralize,
        isOverSeatCount: () => {
          const org = this.state.selectedOrg;
          const users = org && this.state.orgUsers[org.id];
          return users && org[`seat_count`] < users.length + this.state.userRows.length;
        },
        hasBillingPerms: () => this.state.selectedOrg.permissions.includes(Permission.UpdateOrgBilling),
        trackUpsellClick: (ev) => {
          ev.preventDefault();
          track(
            events.clickUpsell,
            {[properties.featureLimit]: properties.featureLimitTeamMembers},
            () => (getLocation().href = ev.target.href),
          );
        },
        getDowngradedProjectsString: (row: UserRow) => {
          return abbreviateStrings(
            row.projectIdsToDowngrade.map(
              (pid) => this.state.orgProjects[this.state.selectedOrg.id].find((proj) => proj.id === pid).name,
            ),
            140,
          );
        },
        getAllDowngradedProjectsString: (row: UserRow) =>
          row.projectIdsToDowngrade
            .map((pid) => this.state.orgProjects[this.state.selectedOrg.id].find((proj) => proj.id === pid).name)
            .join(`, `),
        seeAllDowngradedProjects: (rowIdx) =>
          this.update({
            userRows: produce(this.state.userRows, (userRows) => {
              userRows[rowIdx].seeAll = true;
            }),
          }),

        selectExpirationMode: (ev, row: UserRow) => {
          if (ev.detail.item.value === ExpiresModeOptions.Specify) {
            this.updateUserRow(row, {
              expires: {
                interval: 30,
                unit: ExpiresUnit.Day,
              },
            });
          }
        },
        selectExpiresUnit: (ev, row: UserRow) => {
          const expires = {...row.expires, unit: ev.detail.item.value};
          this.updateUserRow(row, {
            expires,
          });
        },
        updateExpiresInterval: (ev, row: UserRow) => {
          const expires = {...row.expires, interval: parseInt(ev.target.value)};
          this.updateUserRow(row, {
            expires,
          });
        },
      },
    };
  }

  set orgUsers(orgUsers: KeyedByOrg<DetailedUser[]>) {
    this.update({orgUsers});
  }

  set orgProjects(orgProjects: KeyedByOrg<PartialProject[]>) {
    this.update({orgProjects, isOrgProjectsProp: true});

    // Special case where if `orgProjects` is passed in and there is a single project,
    // we explicitly don't render a project selection. This is to streamline the invite flow
    // in the context of a specific project, rather than at an organization level.
    const defaultProjectId = this.getProjectIdIfSingleProject();
    if (defaultProjectId) {
      this.update({
        userRows: this.state.userRows.map((row) => ({...row, projectIds: [defaultProjectId]})),
      });
    }
  }

  set orgs(orgs) {
    this.update({
      orgs,
      selectedOrg: orgs.length === 1 ? orgs[0] : null,
    });
    this.syncData();
  }

  getProjectIdIfSingleProject(): number | null {
    if (!this.state.isOrgProjectsProp) {
      return null;
    }

    const orgProjectsList = Object.values(this.state.orgProjects);
    return orgProjectsList.length === 1 && orgProjectsList[0].length === 1 ? orgProjectsList[0][0].id : null;
  }

  syncData({refetch = []} = {}) {
    const org = this.state.selectedOrg;

    // don't overwrite existing data unless explicitly requested via `refetch` arg
    const shouldUpdateData = ({stateKey}) => !this.state[stateKey][org.id] || refetch.includes(stateKey);

    // wait until a single org is specified to fetch data
    if (org) {
      [
        [`orgUsers`, fetchOrgUserData],
        [`orgRoles`, (orgId) => getOrgAssignableOrgRoles(orgId, this.isServiceAccount())],
        [`orgProjects`, fetchOrgProjectData],
        ...(this.attr(`exclude-teams`) ? [] : [[`orgTeams`, fetchOrgTeamData]]),
        [`orgProjectRoles`, getOrgAssignableProjectRoles],
        [`orgDomains`, fetchDomainData],
      ]
        .filter(([stateKey]: [string]) => shouldUpdateData({stateKey}))
        .map(([stateKey, fetchData]: [string, (orgId: number) => Promise<any>]) => {
          // fetch data asynchronously with `then` instead of `async/await`
          // so that it appears in state incrementally as it becomes available
          return fetchData(org.id).then((data) => {
            if (!shouldUpdateData({stateKey})) {
              return;
            }

            this.update({
              [stateKey]: {
                ...this.state[stateKey],
                [org.id]: data,
              },
            });
          });
        });
    }
  }

  updateUserRow(row: UserRow, data: Partial<UserRow>) {
    this.update({
      userRows: this.state.userRows.map((r) => (r.id !== row.id ? r : {...row, ...data})),
    });
  }

  allRowsValid() {
    const {userRows} = this.state;
    if (this.isServiceAccount()) {
      return userRows.every((row) => !(row.nameError || row.orgRoleError || row.projectError || row.projectRoleError));
    } else {
      return userRows.every((row) => !(row.emailError || row.orgRoleError || row.projectError || row.projectRoleError));
    }
  }

  validateEmail(row: UserRow) {
    // don't show validation errors for empty email
    if (row.email && row.email.length) {
      const verifiedDomain = isVerifiedDomain(row.email, this.state.orgDomains[this.state.selectedOrg.id]);
      const invalidEmail = !isValidEmailAddress(row.email);

      row = {
        ...row,
        emailError: invalidEmail,
        emailInfo: !verifiedDomain && !invalidEmail,
        emailErrorMsg: invalidEmail
          ? null
          : !verifiedDomain
            ? this.t(`settings.org-settings.invite-users.not-in-org-email`)
            : null,
      };
    }
    return row;
  }

  validateName(row: UserRow) {
    if (row.name && row.name.length) {
      const nameErrorMsg = getServiceAccountNameErrorMessage(row.name);
      row = {
        ...row,
        nameError: !!nameErrorMsg,
        nameErrorMsg,
      };
    }
    return row;
  }

  validateOrgRole(row: UserRow) {
    return {
      ...row,
      orgRoleError: !row.orgRole,
    };
  }

  validateProject(row: UserRow) {
    return {
      ...row,
      projectError: this.state.currentTab === TABS.PROJECT && !row.projectIds.length,
    };
  }

  validateProjectRole(row: UserRow) {
    if (this.isServiceAccount()) {
      return {
        ...row,
        projectRoleError: !row.projectRole && Boolean(row.projectIds.length),
      };
    }
    return {
      ...row,
      projectRoleError: this.state.currentTab === TABS.PROJECT && !row.projectRole,
    };
  }

  validateCreateServiceAccountForm() {
    return {
      userRows: this.state.userRows
        .map((row) => this.validateName(row))
        .map((row) => this.validateOrgRole(row))
        .map((row) => this.validateProjectRole(row))
        .map((row) => ({
          ...row, // only validate name length on form submit
          nameError: row.nameError || !(row.name && row.name.length),
        })),
    };
  }

  validateAddUsersForm() {
    return {
      userRows: this.state.userRows
        .map((row) => this.validateEmail(row))
        .map((row) => this.validateOrgRole(row))
        .map((row) => (this.attr(`exclude-project-selections`) ? row : this.validateProject(row)))
        .map((row) => (this.attr(`exclude-project-selections`) ? row : this.validateProjectRole(row)))
        .map((row) => ({
          ...row, // only validate email length on form submit
          emailError: row.emailError || !(row.email && row.email.length),
          emailInfo: row.emailInfo,
        })),
    };
  }

  validateBulkAddForm() {
    return {
      userFile: {
        ...this.state.userFile,
        error: !this.state.userFile.csvFile,
      },
    };
  }

  isServiceAccount() {
    return this.attr(`user-type`) === UserType.ServiceAccount;
  }

  async createServiceAccount(users) {
    // For now, we only create one service account at a time
    const serviceAccount = users[0];
    const projects = serviceAccount.projectIds.map((id) => ({id, role: serviceAccount.projectRole}));
    const expires =
      serviceAccount.expires && moment().add(serviceAccount.expires.unit, serviceAccount.expires.interval);

    const body: any = {
      username: serviceAccount.name,
      expires: expires && expires.toISOString().split(`.`)[0] + `Z`,
      projects,
    };
    // orgRole=member is equitable to no role specified
    if (serviceAccount.orgRole !== UserRole.Member) {
      body.role = serviceAccount.orgRole;
    }

    try {
      const response = await apiClient.post<CreateServiceAccountResult>(
        `/api/app/organizations/${this.state.selectedOrg.id}/service-accounts`,
        body,
      );
      this.update({
        serviceAccount: {
          secret: response.token,
          username: response.username,
        },
      });
      this.trackServiceAccountEvent(events.orgServiceAccountCreated, {successful: true});
    } catch (error) {
      const errorMsg = error.message;
      this.update({error: errorMsg});
    }
  }

  trackServiceAccountEvent(event, props = {}) {
    track(`${event}`, {
      screen: `Service Accounts`,
      ...props,
    });
  }

  // TODO opslead
  async sendInvitation(users) {
    const orgUsersEmail = this.state.orgUsers[this.state.selectedOrg.id]
      .filter((ou) => ou.status === `Active`)
      .map((ou) => ou.email);
    const [existingUsers, usersToInvite] = partition(users, (u) => orgUsersEmail.includes(u.email));

    // sanitize user data and transform project ids/role into project objects
    const userInvites = usersToInvite.map(
      ({email, name, orgRole, projectIds, projectIdsToDowngrade, projectRole, teamNames}) => ({
        email,
        name,
        teamNames,
        // TODO I don't really like that we are hard-coding admin here, but until custom roles are a thing it should work
        projects: projectIds.map((id) => ({id, role: projectIdsToDowngrade.includes(id) ? `admin` : projectRole})),
        // orgRole=member is equitable to no role specified
        ...(orgRole === UserRole.Member ? {} : {orgRole}),
      }),
    );

    let isSuccessful;
    let successfulInvitations = [];
    let failedInvitations = [];

    try {
      await this.addExistingUsers(existingUsers);
      const rawResponse = await addOrgUsers(this.state.selectedOrg.id, userInvites);
      isSuccessful = rawResponse.every((response) => typeof response.error === `undefined`);
      if (isSuccessful) {
        successfulInvitations = rawResponse;
      } else {
        failedInvitations = rawResponse.filter((response) => !response.invited);
      }
    } catch (error) {
      isSuccessful = false;
      let errorMsg;
      try {
        errorMsg = JSON.parse(error.message);
      } catch (_jsonError) {
        errorMsg = [];
      }
      errorMsg.forEach((message) => {
        const response = message.split(`: `);
        if (response.length === 2) {
          const failureMessage = this.t(`settings.org-settings.invite-users.error-inviting-users`, {
            errorMessage: response[0],
          });
          const emails = response[1].split(`\n`);
          failedInvitations = failedInvitations.concat(
            emails.map((email) => ({
              email,
              error: failureMessage,
            })),
          );
        }
      });
      if (!failedInvitations.length) {
        this.dispatchEvent(
          new CustomEvent(`error`, {
            detail: {
              error,
              message: this.t(`settings.org-settings.invite-users.error-inviting-users`, {errorMessage: error.message}),
            },
          }),
        );
      }
    }

    // success
    if (isSuccessful) {
      const numberOfEmails = successfulInvitations.filter((inv) => inv.invited).length;
      const invitationSentence =
        numberOfEmails > 0
          ? this.t(`settings.org-settings.invite-users.email-invite-sent`, {count: numberOfEmails})
          : ``;

      const numberAdded = successfulInvitations.filter((inv) => inv.added).length;
      const addedSentence =
        numberAdded > 0 ? this.t(`settings.org-settings.invite-users.user-added-to-org`, {count: numberAdded}) : ``;

      if (invitationSentence || addedSentence) {
        this.dispatchEvent(
          new CustomEvent(`notify`, {
            detail: {
              message: `${invitationSentence}${addedSentence}`,
            },
          }),
        );
      }

      track(events.inviteUser, {
        [properties.numUsers]: numberOfEmails,
        [properties.type]: `Single`,
        [properties.tab]: `Users`,
      });

      this.syncData({refetch: [`orgUsers`]});
      this.dispatchEvent(new CustomEvent(`save`));

      // failure
    } else {
      // keep modal open, prompt tooltip
      const userRows = this.state.userRows.map((row) => {
        const invitation = failedInvitations.find((currInvitation) => currInvitation.email === row.email);
        if (invitation) {
          // `Already invited` is a hard coded error code from webapp/organization/invite.py
          const errorMessage =
            invitation.error === `Already invited`
              ? this.t(`settings.org-settings.invite-users.already-invited`)
              : invitation.error;
          return {...row, isValidEmail: false, emailErrorMsg: errorMessage, emailError: true, emailInfo: false};
        } else {
          return row;
        }
      });
      this.update({userRows});
    }
  }

  async addExistingUsers(users) {
    const userCount = new Set(users.map((u) => u.email)).size;
    const {projects, teams} = usersToProjectsAndTeams(users);
    if (projects.length > 0) {
      try {
        await addUsersToOrgProjects(this.state.selectedOrg.id, projects);
        // addUsersToOrgTeams
        this.dispatchEvent(
          new CustomEvent(`notify`, {
            detail: {
              message: this.t(`settings.org-settings.invite-users.user-access-updated`, {count: userCount}),
            },
          }),
        );
      } catch (err) {
        reporter.error(err, `Failed updating existing user project grants`);
      }
    }
    if (teams.length > 0) {
      try {
        await addUsersToOrgTeams(
          this.state.selectedOrg.id,
          teams.map(({name, userEmails}) => ({
            id: this.state.orgTeams[this.state.selectedOrg.id].find((t) => t.name === name).id,
            userEmails,
          })),
        );
        // addUsersToOrgTeams
        this.dispatchEvent(
          new CustomEvent(`notify`, {
            detail: {
              message: this.t(`settings.org-settings.invite-users.user-access-updated`, {count: userCount}),
            },
          }),
        );
      } catch (err) {
        reporter.error(err, `Failed updating existing user team grants`);
      }
    }
  }

  async sendCsvFile(attachment) {
    const formData = new FormData();
    formData.append(`csv`, attachment);

    let isSuccessful;
    let successfulInvitations = [];
    let failedInvitations = [];
    let errorMsg;

    try {
      const rawResponse = await addOrgUsersWithAttachment(this.state.selectedOrg.id, formData);
      isSuccessful = rawResponse.every((response) => typeof response.error === `undefined`);
      if (isSuccessful) {
        successfulInvitations = rawResponse;
      } else {
        failedInvitations = rawResponse
          .filter((response) => !response.invited)
          .map((invitation) =>
            this.t(`settings.org-settings.invite-users.cant-invite-error`, {
              email: invitation.email,
              error: invitation.error,
            }),
          );
        errorMsg = failedInvitations.join(`. \n`);
      }
    } catch (error) {
      isSuccessful = false;
      errorMsg = error.message;
    }

    // success
    if (isSuccessful) {
      const numberOfEmails = successfulInvitations.length;
      const invitationsText = pluralize(`invitation`, numberOfEmails);
      const hasText = pluralize(`has`, numberOfEmails, `have`);

      this.dispatchEvent(
        new CustomEvent(`notify`, {
          detail: {
            message: `${numberOfEmails} email ${invitationsText} ${hasText} been sent.`,
          },
        }),
      );

      track(events.inviteUser, {
        [properties.numUsers]: numberOfEmails,
        [properties.type]: `Bulk`,
        [properties.tab]: `Users`,
      });

      this.syncData({refetch: [`orgUsers`]});
      this.dispatchEvent(new CustomEvent(`save`));

      // failure
    } else {
      this.update({
        userFile: {
          ...this.state.userFile,
          error: true,
          errorMsg,
        },
      });
    }
  }

  canInviteToProject(project: PartialProject) {
    // TODO this is a hacky way of checking but will work until custom-roles
    return [
      ...(this.state.orgRoles[this.state.selectedOrg.id] ?? []),
      ...(this.getCurrentOrgProjectData()[project.id] ?? []),
    ]
      .map((role) => role.name)
      .includes(UserRole.Admin);
  }
}

export const InviteUsersModalEl = defineAndMakeJsxFactory<InviteUsersModalJsxProps>(
  `mp-invite-users-modal`,
  InviteUsersModal,
);
export const InviteUsersModalReact = panel2react<InviteUsersModalJsxProps>(`mp-invite-users-modal`, InviteUsersModal);
